/**
 * Kép előkészítése feltöltéshez.
 *
 * A méretezés SZÁNDÉKOSAN ugyanaz, mint az Android appban (2048 px hosszabb
 * él, JPEG q82, ~400 kB/fotó): 4000 mappánál ez ~1,6 GB tárhely, szemben a
 * nyers 4-5 MB-os fotók ~20 GB-jával. Ha a két kliens eltérő méretben
 * töltene fel, ugyanannak a mappának a fotói összemérhetetlenek lennének, és
 * a tárhely-becslés is felborulna.
 */
const MAX_EDGE = 2048;
const JPEG_QUALITY = 0.82;

export interface PreparedImage {
  blob: Blob;
  checksum: string;
  width: number;
  height: number;
}

export async function prepareImage(file: File): Promise<PreparedImage> {
  const bitmap = await createImageBitmap(file);

  const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height));
  const width = Math.round(bitmap.width * scale);
  const height = Math.round(bitmap.height * scale);

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('A böngésző nem tudott 2D rajzfelületet létrehozni.');
  ctx.drawImage(bitmap, 0, 0, width, height);
  bitmap.close();

  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY),
  );
  if (!blob) throw new Error('A kép átalakítása nem sikerült.');

  return { blob, checksum: await sha256Hex(blob), width, height };
}

/**
 * SHA-256 hexa lenyomat. A szerver ezt kéri (`^[a-f0-9]{64}$`), és ez adja
 * a duplikátum-védelmet is (`IMAGE_DUPLICATE_CHECKSUM`): ugyanaz a fotó
 * kétszer nem kerül fel ugyanahhoz a mappához.
 *
 * A lenyomatot a MÁR ÁTALAKÍTOTT blobból számoljuk, nem az eredeti fájlból —
 * az kerül fel, tehát annak a lenyomata a hiteles.
 */
async function sha256Hex(blob: Blob): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', await blob.arrayBuffer());
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}
